/**
 * Special Factors Form Component
 */

import React from 'react';
import { FormData } from '../types';

interface SpecialFactorsFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onBack: () => void;
  loading: boolean;
}

const SpecialFactorsForm: React.FC<SpecialFactorsFormProps> = ({ formData, onChange, onNext, onBack, loading }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Special Factors
      </h2>

      <p className="text-gray-600 mb-6">
        These factors may affect the court's decision on property division.
      </p>

      <div className="space-y-6">
        {/* Asset Waste by Spouse */}
        <div className="flex items-start gap-4">
          <input
            type="checkbox"
            id="spouseWasted"
            checked={formData.spouseWastedAssets || false}
            onChange={(e) => onChange('spouseWastedAssets', e.target.checked)}
            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <label htmlFor="spouseWasted" className="block text-sm font-medium text-gray-900">
              Spouse wasted assets (gambling, excessive spending, etc.)
            </label>
            <p className="text-sm text-gray-500 mt-1">
              Has your spouse wasted or destroyed marital assets?
            </p>
          </div>
        </div>

        {/* Asset Waste by User */}
        <div className="flex items-start gap-4">
          <input
            type="checkbox"
            id="userWasted"
            checked={formData.userWastedAssets || false}
            onChange={(e) => onChange('userWastedAssets', e.target.checked)}
            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <label htmlFor="userWasted" className="block text-sm font-medium text-gray-900">
              I have wasted assets (gambling, excessive spending, etc.)
            </label>
            <p className="text-sm text-gray-500 mt-1">
              Have you wasted or destroyed marital assets?
            </p>
          </div>
        </div>

        {/* User Stay-At-Home */}
        <div className="flex items-start gap-4">
          <input
            type="checkbox"
            id="stayAtHome"
            checked={formData.userStayAtHome || false}
            onChange={(e) => onChange('userStayAtHome', e.target.checked)}
            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <label htmlFor="stayAtHome" className="block text-sm font-medium text-gray-900">
              I was a stay-at-home spouse
            </label>
            <p className="text-sm text-gray-500 mt-1">
              Did you sacrifice your career to care for the home and family?
            </p>
          </div>
        </div>

        {/* User Supported Spouse Career */}
        <div className="flex items-start gap-4">
          <input
            type="checkbox"
            id="supportedCareer"
            checked={formData.userSupportedSpouseCareer || false}
            onChange={(e) => onChange('userSupportedSpouseCareer', e.target.checked)}
            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <label htmlFor="supportedCareer" className="block text-sm font-medium text-gray-900">
              I supported spouse's career/education
            </label>
            <p className="text-sm text-gray-500 mt-1">
              Did you help your spouse advance their career or education?
            </p>
          </div>
        </div>

        {/* Additional Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Special Circumstances (Optional)
          </label>
          <textarea
            value={formData.specialFactorsDescription || ''}
            onChange={(e) => onChange('specialFactorsDescription', e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe any other special circumstances that may affect property division..."
          />
        </div>

        {/* Information Box */}
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="font-semibold text-purple-900 mb-2">
            💡 How These Factors Matter
          </h3>
          <p className="text-sm text-purple-800">
            Courts consider various factors when dividing property. These include:
            Asset dissipation (waste), career sacrifices, and contributions to the marriage.
            Document any relevant circumstances carefully.
          </p>
        </div>

        {/* Legal Disclaimer */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <h3 className="font-semibold text-yellow-800 mb-2">
            ⚠️ Legal Consideration
          </h3>
          <p className="text-sm text-yellow-700">
            Be honest in your responses. False claims can harm your case in court.
            Courts verify these factors through evidence and testimony.
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={loading}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Calculating...' : 'View Preview'}
        </button>
      </div>
    </div>
  );
};

export default SpecialFactorsForm;